// @ts-check

import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppBridge } from "@shopify/app-bridge-react";
import {
  Card,
  Layout,
  TextField,
  Page,
  BlockStack,
  Tabs,
  Box,
  InlineStack,
  Button,
  Banner,
} from "@shopify/polaris";
import { ExportIcon, RefreshIcon } from "@shopify/polaris-icons";

import { usePostHog } from "posthog-js/react";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { SavedUtmLinksTable } from "components/SavedUtmLinksTable/SavedUtmLinksTable";
import { CreateRegularUtmLink } from "components/CreateUtmLinkCard/CreateRegularUtmLink";
import { CreateFacebookUtmLink } from "components/CreateUtmLinkCard/CreateFacebookUtmLink";

import { TABS, useUtmLinks, UtmLinksProvider } from "context/UtmLinksContext";
import { useGlobalContext } from "context/GlobalContext";

const PureGenerateUtmLinksPage = () => {
  const navigate = useNavigate();
  const shopify = useAppBridge();
  const posthog = usePostHog();
  const {
    error,
    setError,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    tabIndex,
    setTabIndex,
    selectedTab,
    generatedLink,
  } = useUtmLinks();
  const {
    isRefetchingSavedUtmSets,
    refetchSavedUtmSets,
  } = useGlobalContext();

  useEffect(() => {
    posthog.capture("$pageview");

    return () => {
      setError(null);
    }
  }, []);

  const selectTabHandler = (i) => {
    setTabIndex(i);
    posthog.capture("utm-generator-tab-change", { tab: TABS[i].id });
  };

  const handleCopyFullUrl = () => {
    shopify.toast.show("Copied to Clipboard");
    posthog.capture("full-url-copied");
  };

  const handleExportClick = () => {
    navigate("/utm-generator/export");
  };

  let errorBanner;
  if (error) {
    errorBanner = (
      <Layout.Section>
        <Banner
          title="Error"
          tone="critical"
        >
          <p>{error}</p>
        </Banner>
      </Layout.Section>
    );
  }

  return (
    <Page
      title="Generate UTM Link"
      fullWidth
    >
      <Layout>
        {errorBanner}

        <Layout.Section>
          <BlockStack gap="200">
            <InlineStack gap="200" align="space-between">
              <Button
                icon={RefreshIcon}
                loading={isRefetchingSavedUtmSets}
                onClick={refetchSavedUtmSets}
              >
                Refresh UTM links
              </Button>

              <Button
                icon={ExportIcon}
                onClick={handleExportClick}
              >
                Export UTM links
              </Button>
            </InlineStack>

            <SavedUtmLinksTable />
          </BlockStack>
        </Layout.Section>

        <Layout.Section variant="oneThird">
          <div style={{ minHeight: 864.5 }}>
            <div style={{ position: "sticky", top: 0 }}>
              <BlockStack gap="400">
                <Card>
                  <BlockStack gap="200">
                    <TextField
                      label="Generated UTM Link"
                      value={generatedLink}
                      autoComplete="off"
                      readOnly
                      labelHidden
                    />

                    <InlineStack align="center">
                      <CopyToClipboard
                        text={generatedLink}
                        onCopy={handleCopyFullUrl}
                      >
                        <Button
                          disabled={
                            !utmSource &&
                            !utmMedium &&
                            !utmCampaign &&
                            !utmContent &&
                            !utmTerm
                          }
                          variant="secondary"
                        >
                          Copy URL
                        </Button>
                      </CopyToClipboard>
                    </InlineStack>
                  </BlockStack>
                </Card>

                <Card padding="0">
                  <Tabs
                    tabs={TABS}
                    selected={tabIndex}
                    onSelect={selectTabHandler}
                  />

                  <Box padding="400">
                    {selectedTab.id === "custom" ? (
                      <CreateRegularUtmLink />
                    ) : (
                      <CreateFacebookUtmLink />
                    )}
                  </Box>
                </Card>
              </BlockStack>
            </div>
          </div>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default function GenerateUtmLinksPage() {
  return (
    <UtmLinksProvider>
      <PureGenerateUtmLinksPage />
    </UtmLinksProvider>
  );
};
